/**
 * @file: MemberChatButton.tsx
 *
 * A small "start chat" icon button shown beside a member reference (a task
 * assignee, a technician, a service advisor, …). Opens a 1:1 floating chat
 * with that org member. Renders nothing for the current user — you can't chat
 * with yourself.
 */

import { ChatLaunchIcon } from '@euler/app/flows/chat/components/ChatLaunchIcon';
import { useFloatingChat } from '@euler/app/flows/chat/lib/floating/context';
import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { FC, memo, useCallback } from 'react';
import { StyleProp, TouchableOpacity, ViewStyle } from 'react-native';

const kHitSlop = { top: 8, right: 8, bottom: 8, left: 8 };

export const MemberChatButton: FC<{
  orgMemberId: number;
  size?: number;
  style?: StyleProp<ViewStyle>;
}> = memo(({ orgMemberId, size = 16, style }) => {
  const { me } = useChatSession();
  const { openChat } = useFloatingChat();
  const onPress = useCallback(
    () => openChat({ kind: 'member', orgMemberId }),
    [openChat, orgMemberId],
  );
  // Can't chat with yourself.
  if (orgMemberId === me.user.memberId) return null;
  return (
    <TouchableOpacity onPress={onPress} hitSlop={kHitSlop} style={style}>
      <ChatLaunchIcon size={size} />
    </TouchableOpacity>
  );
});
